/**
 * Error page experiences + the failure hooks the acceptance tests drive.
 *
 * Two levels of resilience:
 * - `ErrorPage` — styled error page rendered by the page-content error
 *   boundary. It appears INSIDE MarketingLayout, so the visitor keeps the main
 *   navigation and footer and can recover anywhere.
 * - `MinimalErrorPage` — rendered by the layout-level boundary when the shared
 *   layout itself fails. Fully standalone (no nav/footer), so the visitor is
 *   never left with a blank screen.
 *
 * Neither page renders the caught error — stack traces, error codes and
 * internal details must never reach visitors.
 *
 * `TestFailureTrigger` backs the `/__test-fail/*` routes the acceptance web
 * driver navigates to in order to exercise both boundaries.
 */
import { Link } from "react-router";
import { buttonVariants } from "@diality/ui";

export function ErrorPage() {
  return (
    <section className="text-brand-dark-gray mx-auto flex flex-col items-center gap-4 px-6 pb-24 text-center sm:pb-32 pt-43.5 max-[1160px]:pt-35">
      <h1 className="text-5xl font-display font-light leading-[104%] tracking-[-1.92px] sm:text-8xl">
        Something Went Wrong
      </h1>
      <p className="text-xl font-sans font-light leading-[155%] max-w-135.5 pb-2">
        Something went wrong on our end — it&rsquo;s not you. You can try
        reloading the page, or head back to the homepage and keep exploring.
      </p>
      <Link to="/" className={buttonVariants({ size: "sm" })}>
        Back to Homepage
      </Link>
    </section>
  );
}

export function MinimalErrorPage() {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background px-6 text-center text-foreground">
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
        Something went wrong
      </h1>
      <p className="max-w-md text-lg text-muted-foreground">
        Something went wrong on our end — it&rsquo;s not you. Please head back
        to the homepage and try again.
      </p>
      {/* Plain anchor: when the layout itself failed we don't rely on any of
          the shared chrome — a full reload to "/" is the most robust way back. */}
      <a href="/" className={buttonVariants({ size: "lg" })}>
        Back to home
      </a>
    </main>
  );
}

/** Route element that always throws during render — acceptance-test hook. */
export function TestFailureTrigger(): React.ReactElement {
  throw new Error(
    "Intentional failure — acceptance-test hook for the error page experiences",
  );
}
